import { Dialog } from 'primereact/dialog'
import React from 'react'
import classes from './PmrEcdAlignmentConfirmDialog.module.scss'
import { E2EProjectLineECDAdjustmentModel } from '../../../../../shared/models/service-models/E2EProjectLineECDAdjustmentModel'

import { Column } from 'primereact/column'
import { DataTable } from 'primereact/datatable'
import { format } from 'date-fns'
import { Button } from 'primereact/button'

interface PmrEcdAlignmentConfirmDialogProps {
  isVisible: boolean
  isLoading: boolean
  isLoadingAlign: boolean
  isOrderLineEcd: boolean
  ecdAlignmentDetails?: E2EProjectLineECDAdjustmentModel[]

  onClose(): void
  onSubmit(isAlign: boolean, isForceRecalibrate: boolean): void
}

const PmrEcdAlignmentConfirmDialog = (props: PmrEcdAlignmentConfirmDialogProps) => {
  //#region Constants
  const ecdTooltips = ['Order Line ECD', 'FLEX Project ECD']
  const headerTitle = props.ecdAlignmentDetails
    ? `Align ${props.isOrderLineEcd ? ecdTooltips[1] : ecdTooltips[0]}?`
    : `${props.isOrderLineEcd ? ecdTooltips[1] : ecdTooltips[0]} Must Be Updated`
  const dialogWidth = props.ecdAlignmentDetails ? '60%' : '45%'
  //#endregion

  //#region Inline Components
  const InlineBody = () => {
    const newECDText = props.isOrderLineEcd ? ecdTooltips[0] : ecdTooltips[1]
    const currentECDText = props.isOrderLineEcd ? ecdTooltips[1] : ecdTooltips[0]
    if (props.ecdAlignmentDetails) {
      return (
        <>
          <div className={classes['dialog-text']}>
            <p>
              The new {newECDText} is different from the current {currentECDText}.
            </p>
            <p>Do you want to align the {currentECDText} with this new value?</p>
          </div>
          <DataTable
            value={props.ecdAlignmentDetails}
            responsiveLayout="scroll"
            className={classes['alignment-table']}
            size="small"
            showGridlines={true}
          >
            <Column field="e2EProjectName" header="GMAWB Project Name" />
            <Column field="orderlineDescription" header="Order Line Number" />
            <Column
              field="currentECD"
              header={`Current ${props.isOrderLineEcd ? ecdTooltips[1] : ecdTooltips[0]}`}
              body={(data: E2EProjectLineECDAdjustmentModel) => {
                const transformDate = data.currentECD ? format(data.currentECD, 'dd MMM yyyy') : ''
                return transformDate
              }}
            />
            <Column
              field="adjustedECD"
              header={`Adjusted ${props.isOrderLineEcd ? ecdTooltips[1] : ecdTooltips[0]}`}
              body={(data: E2EProjectLineECDAdjustmentModel) => {
                const transformDate = data.adjustedECD
                  ? format(data.adjustedECD, 'dd MMM yyyy')
                  : ''
                return transformDate
              }}
            />
          </DataTable>

          <p>
            <em style={{ color: 'red' }}>
              * Updating or aligning the Order Line ECD will recalibrate your order line's ongoing
              schedule.
            </em>
          </p>
        </>
      )
    } else {
      const isLaterText = props.isOrderLineEcd ? 'later' : 'earlier'
      return (
        <div className={classes['dialog-body']}>
          <p>
            The new {newECDText} is {isLaterText} than the current {currentECDText}.
          </p>
          <p>
            To proceed, the system will automatically update the {currentECDText} to match the new
            date. Do you want to continue?
          </p>
          <p>
            <em style={{ color: 'red' }}>
              * Updating or aligning the Order Line ECD will recalibrate your order line's ongoing
              schedule.
            </em>
          </p>
        </div>
      )
    }
  }

  const InlineFooter = () => {
    if (props.ecdAlignmentDetails) {
      return (
        <div className={classes['dialog-footer']}>
          <Button
            disabled={props.isLoading || props.isLoadingAlign}
            onClick={props.onClose}
            className={classes['btn-cancel']}
          >
            CANCEL
          </Button>

          <Button
            disabled={props.isLoadingAlign}
            loading={props.isLoading}
            onClick={() => props.onSubmit(false, false)}
            className={classes['btn-not-align-save']}
          >
            SAVE WITHOUT ALIGNING
          </Button>

          <Button
            disabled={props.isLoading}
            loading={props.isLoadingAlign}
            onClick={() => props.onSubmit(true, false)}
            className={classes['btn-align-save']}
          >
            ALIGN AND SAVE
          </Button>
        </div>
      )
    } else {
      return (
        <div className={classes['dialog-footer']}>
          <Button
            disabled={props.isLoading || props.isLoadingAlign}
            onClick={props.onClose}
            className={classes['btn-cancel']}
          >
            CANCEL
          </Button>

          <Button
            loading={props.isLoadingAlign}
            onClick={() => props.onSubmit(true, false)}
            className={classes['btn-proceed']}
          >
            PROCEED
          </Button>
        </div>
      )
    }
  }

  //#endregion

  return (
    <Dialog
      className={classes['custom-dialog']}
      header={headerTitle}
      footer={<InlineFooter />}
      draggable={false}
      closeOnEscape={false}
      visible={props.isVisible}
      onHide={props.onClose}
      style={{ width: dialogWidth, maxWidth: dialogWidth, overflowY: 'auto' }}
    >
      <hr className={classes['modal-header-hr']} />
      <div className={classes['body-container']}>
        <InlineBody />
      </div>
    </Dialog>
  )
}

export default PmrEcdAlignmentConfirmDialog
